import { type FC, memo } from "react";

import { Tag, Section, Card, Button } from "@snapchat/snap-design-system";
import { TrashIcon } from "@snapchat/snap-design-system-icons";

import {
    CopyToClipboardButton,
    EditableText,
    LinkBack,
    Pipe,
    UnsavedChangesPrompt,
    Visible,
    sectionNavigationContainer,
    sectionSubtitleStyle,
    sectionTitleStyle,
    groupDescriptionCss,
} from "../../../common/components";
import { Flexbox } from "../../../core/components";
import { AddLensesModal } from "../../../lenses/components";
import { useModal } from "../../../core/hooks";
import { EditableTextArea } from "../../../common/components/EditableTextArea";
import { NoResults } from "../../components/NoResults";
import { type LensUpdateData, type GroupDetailsCommon } from "../models";
import { DeleteLensGroupModalContainer } from "../../containers";
import type { LensListItemModel } from "../../../lenses/models";
import { formatMediumDateTime } from "../../../common/formatters";
import { sectionStyle } from "../../../common/styles/Section.styles";
import { idIsCopiedToClipboard } from "../../../common/model";
import type { Permissions } from "../../../gql-generated/aliases";
import { ActionDisabledTooltip, DisabledButtonToolipFix } from "../../../common/components/ActionDisabledTooltip";

import { rightHeaderContainer, addLensButtonCss, defaultButtonCss } from "./GroupDetails.styles";
import { GroupLensesList } from "./GroupLensesList";

export interface GroupDetailsViewProps {
    group: Readonly<GroupDetailsCommon>;
    organizationId: string;
    saving: boolean;
    loading: boolean;
    isError: boolean;
    readonly: boolean;
    onSaveChanges: () => void;
    onAddLenses: (lenses: LensListItemModel[]) => void;
    onRemoveLens: (lensId: string) => void;
    onSearch: (lensName: string) => void;
    onLensInGroupChanged: (updatedData: LensUpdateData) => void;
    onLensGroupSuccessfullyDeleted: () => void;
    onNameChanged: (name: string) => void;
    onDescriptionChanged: (description: string) => void;
    permissions: Permissions;
}

export const GroupDetailsView: FC<GroupDetailsViewProps> = memo(
    ({
        group,
        loading,
        saving,
        readonly,
        onSaveChanges,
        onAddLenses,
        onRemoveLens,
        organizationId,
        onSearch,
        onLensInGroupChanged,
        isError,
        onLensGroupSuccessfullyDeleted,
        onNameChanged,
        onDescriptionChanged,
        permissions,
    }) => {
        const [addLensModalProps, showAddLensModal] = useModal(false);
        const [deleteLensGroupProps, showDeleteLensGroupModal] = useModal();

        const { filteredLenses, nameFilterValue, info } = group ?? {};
        const { id, name, description, updatedAt, isArchived, readOnly } = info ?? {};

        const isFieldDisabled = loading || saving || readonly || !permissions?.updateGroups;

        return (
            <div className={sectionNavigationContainer}>
                <div>
                    <LinkBack className={sectionNavigationContainer} to=".." relative="path">
                        &lt; Back to Lens Groups
                    </LinkBack>
                </div>
                <Visible when={isError}>
                    <Card titleBorder={false}>
                        <NoResults description="Group not found" />
                    </Card>
                </Visible>
                <Visible when={!isError}>
                    <Section
                        padBody={false}
                        className={sectionStyle}
                        title={
                            <>
                                <Flexbox alignItems="center">
                                    <EditableText
                                        className={sectionTitleStyle}
                                        hasPermission={permissions?.updateGroups ?? false}
                                        initialValue={name}
                                        onNewValue={onNameChanged}
                                        isDisabled={isFieldDisabled}
                                    />
                                    <Visible when={isArchived}>
                                        <Tag size="small" status="error">
                                            Removed
                                        </Tag>
                                    </Visible>
                                    <Visible when={!isArchived && readOnly}>
                                        <Tag size="small">Read Only</Tag>
                                    </Visible>
                                </Flexbox>
                                <Flexbox>
                                    <EditableTextArea
                                        className={groupDescriptionCss}
                                        hasPermission={permissions?.updateGroups ?? false}
                                        value={description}
                                        onChange={onDescriptionChanged}
                                        placeholder="Add a short description to your group"
                                        isDisabled={isFieldDisabled}
                                        maxLength={150}
                                    />
                                </Flexbox>
                            </>
                        }
                        subtitle={
                            <Flexbox className={sectionSubtitleStyle}>
                                Last Updated: {formatMediumDateTime(updatedAt)}
                                <Pipe />
                                Lens Group ID: {id}
                                <CopyToClipboardButton
                                    copyValue={id}
                                    notification={idIsCopiedToClipboard}
                                    tooltip="Copy Lens Group ID"
                                />
                            </Flexbox>
                        }
                        rightHeaderContent={
                            <Flexbox className={rightHeaderContainer}>
                                <ActionDisabledTooltip
                                    placement="left"
                                    hasPermission={permissions?.updateGroups ?? false}
                                >
                                    <DisabledButtonToolipFix
                                        onClick={showDeleteLensGroupModal}
                                        shape="circle"
                                        isDisabled={readonly || !permissions?.updateGroups}
                                        data-testid="group-details-delete-group"
                                    >
                                        <TrashIcon />
                                    </DisabledButtonToolipFix>
                                </ActionDisabledTooltip>
                                <Button
                                    onClick={onSaveChanges}
                                    isDisabled={loading || !group?.isValid || !group?.isChanged || readonly}
                                    isPending={saving}
                                    className={defaultButtonCss}
                                >
                                    Save Changes
                                </Button>
                                <ActionDisabledTooltip
                                    placement="left"
                                    hasPermission={permissions?.scheduleLenses ?? false}
                                >
                                    <DisabledButtonToolipFix
                                        className={addLensButtonCss}
                                        isDisabled={loading || saving || readonly || !permissions?.scheduleLenses}
                                        type="primary"
                                        onClick={showAddLensModal}
                                        data-testid="group-details-add-lens"
                                    >
                                        Add Lens
                                    </DisabledButtonToolipFix>
                                </ActionDisabledTooltip>
                            </Flexbox>
                        }
                    >
                        <GroupLensesList
                            lensesInGroup={filteredLenses}
                            loading={loading}
                            onRemove={onRemoveLens}
                            groupId={id}
                            organizationId={organizationId}
                            onSearch={onSearch}
                            searchValue={nameFilterValue}
                            onLensInGroupChanged={onLensInGroupChanged}
                            readonly={loading || saving || readonly}
                        />
                    </Section>
                    <AddLensesModal
                        {...addLensModalProps}
                        onAddLenses={onAddLenses}
                        alreadySelectedIds={new Set(group?.getLensesIds())}
                    />
                    <DeleteLensGroupModalContainer
                        group={group?.info}
                        {...deleteLensGroupProps}
                        onLensGroupSuccessfullyDeleted={onLensGroupSuccessfullyDeleted}
                    />
                    <UnsavedChangesPrompt when={group?.isChanged} />
                </Visible>
            </div>
        );
    },
);
